import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { dailyReportAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui'
import { formatMoney } from './DailyReportTable'
import AttributionCreditTable, { ATTRIBUTION_CHANNEL_LABELS } from './AttributionCreditTable'
import AttributionModelDetails from './AttributionModelDetails'
import AttributionWindowControl, { AttributionWindowExplanation } from './AttributionWindowControl'
import { DEFAULT_LOOKBACK } from '../lib/attributionLookback'

const orderType = bucket => bucket === 'new' ? 'New order' : bucket === 'recurring' ? 'Renewal' : 'Order type unknown'
const time = timestamp => new Date(timestamp).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })

export default function JourneyAttributionModels({ journey }) {
  const { currentClinicId } = useAuthStore()
  const ids = [...new Set((journey.user_ids || []).filter(id => /^[a-f0-9]{24}$/i.test(id)))]
  const [chosenPatient, setChosenPatient] = useState('')
  const patientId = ids.length === 1 ? ids[0] : ids.includes(chosenPatient) ? chosenPatient : ''
  const lookbackDays = DEFAULT_LOOKBACK // all time for every brand; not a choice on the page
  const [attributionWindow, setAttributionWindow] = useState('all')
  const [metric, setMetric] = useState('revenue')
  const [responseData, setData] = useState(null)
  const data = responseData?.patientId === patientId && responseData?.lookbackDays === lookbackDays && responseData?.attributionWindow === attributionWindow ? responseData : null
  const [selectedCharge, setSelectedCharge] = useState('')
  const [error, setError] = useState(null)
  const [retry, setRetry] = useState(0)
  const supported = Boolean(currentClinicId)

  useEffect(() => {
    let active = true
    setData(null)
    setError(null)
    if (supported && patientId) {
      dailyReportAPI.getCustomerAttribution(patientId, { lookbackDays, attributionWindow })
        .then(response => { if (active) setData(response.data.data) })
        .catch(err => { if (active) setError(err.response?.data?.error?.message || 'Failed to load customer attribution') })
    }
    return () => { active = false }
  }, [supported, currentClinicId, patientId, lookbackDays, attributionWindow, retry])

  if (!supported) return null
  const charge = data?.charges.find(item => item.id === selectedCharge) || data?.charges.find(item => item.bucket === 'new') || data?.charges[0]
  const scope = charge?.bucket === 'new' ? 'new' : 'all'
  const control = 'h-9 rounded-md border border-input bg-background px-3 text-sm max-w-full'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Attribution for this customer</CardTitle>
        <p className="text-sm text-muted-foreground">Actual paid orders and recorded patient sources, calculated by the same models as the Attribution page. These sources can differ from the Segment pageviews in the timeline below.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {ids.length === 0 ? <p className="text-sm text-muted-foreground">No linked customer ID is available for this journey. Model results require a confirmed customer and a paid order.</p> : (
          <>
            <div className="flex flex-wrap gap-4">
              <AttributionWindowControl value={attributionWindow} onChange={setAttributionWindow} />
              {ids.length > 1 && <label className="flex flex-col gap-1 text-xs text-muted-foreground">Customer — multiple IDs linked
                <select className={control} value={patientId} onChange={event => { setChosenPatient(event.target.value); setSelectedCharge('') }}>
                  <option value="">Choose a customer</option>
                  {ids.map(id => <option key={id} value={id}>{id}</option>)}
                </select>
              </label>}
              {charge && <label className="flex flex-col gap-1 text-xs text-muted-foreground">Paid order
                <select className={control} value={charge.id} onChange={event => setSelectedCharge(event.target.value)}>
                  {data.charges.map(item => <option key={item.id} value={item.id}>{time(item.timestamp)} ET · {formatMoney(item.revenue)} · {orderType(item.bucket)}</option>)}
                </select>
              </label>}
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">Show
                <select className={control} value={metric} onChange={event => setMetric(event.target.value)}>
                  <option value="revenue">Revenue credit</option><option value="orders">Fractional order credit</option>
                </select>
              </label>
            </div>
            {error ? <div role="alert" className="flex items-center gap-3 text-sm text-destructive">{error}<Button variant="outline" size="sm" onClick={() => setRetry(value => value + 1)}>Retry</Button></div>
              : patientId && !data ? <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading paid orders…</p>
                : data && !charge ? <p className="text-sm text-muted-foreground">No eligible paid orders found for this customer. Zero-dollar fills and credit-settled shipments do not count as sales.</p> : null}
            {charge && <>
              <p className="text-sm"><strong>{formatMoney(charge.revenue)}</strong> actual revenue · 1 paid order · {orderType(charge.bucket)}</p>
              <AttributionWindowExplanation />
              <AttributionCreditTable comparison={charge.comparison} metric={metric} lookbackDays={lookbackDays} scope={scope} />
              <p className="text-xs text-muted-foreground">Multi gives full credit independently to each channel, so its total can exceed this order’s revenue. Other models split or assign this single order. Open a model heading for its rules.</p>
              <details className="rounded-md border p-3 text-sm">
                <summary className="cursor-pointer font-medium">Touches used for this order ({charge.touches.length})</summary>
                {charge.touches.length ? <ol className="mt-3 space-y-2">{charge.touches.map((touch, index) => <li key={index} className="flex flex-wrap gap-x-3 gap-y-1">
                  <span className="font-medium">{ATTRIBUTION_CHANNEL_LABELS[touch.channel] || touch.channel}</span>
                  <span className="text-muted-foreground">{time(touch.timestamp)} ET</span>
                  <span className="text-muted-foreground">{touch.basis === 'price_book' ? 'Added from price book' : touch.basis === 'bigquery' ? 'BigQuery click (not recorded in core)' : 'Recorded visit'}{touch.closing ? ' · Price-book closer' : ''}{attributionWindow !== 'all' && charge.timestamp - touch.timestamp > attributionWindow * 86400000 ? ' · Outside attribution window' : ''}</span>
                </li>)}</ol> : <p className="mt-2 text-muted-foreground">No eligible visits or closing evidence. This order remains unattributed.</p>}
              </details>
              {charge.comparison.totals.unclassified_funnel_orders > 0 && <p className="text-sm text-muted-foreground">Clicks-Only cannot classify every channel in this journey and leaves the order unattributed.</p>}
              <p className="text-xs text-muted-foreground">Only visits within the lookback and at or before payment qualify. Sales Team and Reddit price books supply closing evidence for new orders only; matching visits are reused. Price books reflect the current patient record.</p>
              <div className="flex flex-wrap gap-3">{charge.comparison.unavailable.map(model => <div key={model.id}><AttributionModelDetails model={model} lookbackDays={lookbackDays} scope={scope} attributionWindow={attributionWindow} /><p className="mt-1 text-xs text-muted-foreground">Unavailable</p></div>)}</div>
            </>}
          </>
        )}
      </CardContent>
    </Card>
  )
}
